import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useCurrentUserModule } from '@/hooks/useUserModule';
import { useAuth } from '@/contexts/AuthContext';
import { useNoGuard } from '@/hooks/useNoGuard';
import { Loader2 } from 'lucide-react';

interface ModuleProtectedRouteProps {
  children: React.ReactNode;
  requiredScreenType?: string;
  requiredSurveyType?: 'pulse' | 'shape';
}

/**
 * Protects routes by ensuring users can only access their current module
 * Redirects to /module if they try to access a different module
 */
export function ModuleProtectedRoute({ 
  children, 
  requiredScreenType,
  requiredSurveyType 
}: ModuleProtectedRouteProps) {
  const location = useLocation();
  const noGuard = useNoGuard();
  const { authenticated, isLoading: authLoading } = useAuth();
  const { data: currentModule, isLoading: moduleLoading } = useCurrentUserModule();
  const [isValidating, setIsValidating] = useState(true);

  useEffect(() => {
    if (!authLoading && !moduleLoading) {
      setIsValidating(false);
    }
  }, [authLoading, moduleLoading]);

  // In noguard mode, bypass all protection
  if (noGuard) {
    return <>{children}</>;
  }

  // Show loading while checking auth and module
  if (authLoading || moduleLoading || isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!authenticated) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  // Always allow access to /updates and profile routes (they have their own internal logic)
  if (location.pathname === '/updates' || 
      location.pathname === '/profile/view' || 
      location.pathname === '/profile/edit') {
    return <>{children}</>;
  }

  // If no current module, user may have completed everything or be a new user
  // Allow access to /updates (don't create redirect loop)
  if (!currentModule) {
    // Only redirect if they're not already trying to access /updates
    if (location.pathname !== '/updates') {
      return <Navigate to="/updates" />;
    }
    // If they're already on /updates, let them access it
    return <>{children}</>;
  }

  // Check if the current route matches the user's current module
  const moduleScreenType = currentModule.module.screenType;
  const moduleSurveyId = currentModule.module.surveyId;

  // Validate access based on current module
  let isAuthorized = false;

  switch (moduleScreenType) {
    case 'profile':
      isAuthorized = location.pathname.startsWith('/profile');
      break;
      
    case 'survey_intro': {
      // Survey intro modules don't have surveyId, check module name or data
      const moduleData = (() => {
        try {
          return JSON.parse(currentModule.module.data || '{}');
        } catch {
          return {};
        }
      })();
      
      // Determine which survey intro based on module name or data
      if (moduleData.route) {
        isAuthorized = location.pathname === moduleData.route;
      } else if (currentModule.module.name.toLowerCase().includes('pulse')) {
        isAuthorized = location.pathname === '/survey/pulse';
      } else if (currentModule.module.name.toLowerCase().includes('shape')) {
        isAuthorized = location.pathname === '/survey/shape';
      } else {
        // If we can't determine, check the path itself
        isAuthorized = location.pathname === '/survey/pulse' || 
                      location.pathname === '/survey/shape';
      }
      break;
    }
      
    case 'survey':
      // Check if they're on the correct survey
      if (moduleSurveyId === 1) {
        isAuthorized = location.pathname.startsWith('/survey/pulse/') && 
                      !location.pathname.includes('shape');
      } else if (moduleSurveyId === 2) {
        isAuthorized = location.pathname.startsWith('/survey/shape/') && 
                      !location.pathname.includes('pulse');
      }
      break;
      
      
    case 'chat':
    case 'coaching':
      isAuthorized = location.pathname.startsWith('/coaching');
      break;
  }

  // Additional checks for specific requirements
  if (requiredScreenType && moduleScreenType !== requiredScreenType) {
    isAuthorized = false;
  }

  if (requiredSurveyType) {
    const currentSurveyType = moduleSurveyId === 1 ? 'pulse' : 'shape';
    if (currentSurveyType !== requiredSurveyType) {
      isAuthorized = false;
    }
  }

  // Allow access to results pages if they've completed that survey
  if (location.pathname.includes('/results')) {
    // This is ok - results pages have their own logic to find completed modules
    return <>{children}</>;
  }

  // If not authorized, redirect to their current module
  if (!isAuthorized) {
    console.log('Access denied - redirecting to current module', {
      attempted: location.pathname,
      currentModule: moduleScreenType,
      moduleSurveyId
    });
    return <Navigate to="/module" />;
  }

  // Authorized - render the protected content
  return <>{children}</>;
}

export default ModuleProtectedRoute;